import { CountryCode } from "@/constants/CountryOptions";
import { AdSuburbViewModel } from "@/sell/models/AdSuburb";
import { Loader } from "@googlemaps/js-api-loader";
import { useEffect, useState } from "react";

const API_KEY = "AIzaSyDul5wJkDPgk66MssJk4lGknWgm9Ic_7Os";

const loader = new Loader({
  apiKey: API_KEY,
  retries: 3,
});

async function getPlaceComponent(
  newPlace: google.maps.places.PlaceResult,
  geocoder: google.maps.Geocoder | undefined,
  fallbackToLocation: boolean,
  ...types: string[]
): Promise<google.maps.GeocoderAddressComponent | undefined> {
  const components = types.map((type) =>
    newPlace.address_components?.find((x) => x.types.includes(type))
  );

  let firstMatch = components.find((x) => x);

  if (!firstMatch && fallbackToLocation) {
    if (!geocoder) {
      console.error("CLIENT_KNOWN_ERROR: google places - geocoder not loaded for fallback");
      return undefined;
    }
    const request: google.maps.GeocoderRequest = {
      location: {
        lat: newPlace.geometry?.location?.lat() ?? 0,
        lng: newPlace.geometry?.location?.lng() ?? 0,
      },
    };

    const geocodeResult = await geocoder.geocode(request);

    const flatAddressComponents = geocodeResult.results.flatMap(
      (x) => x.address_components
    );

    firstMatch = flatAddressComponents.find((x) =>
      types.some((type) => x.types.includes(type))
    );
  }

  return firstMatch;
}

export function useGooglePlaces({
  countryCode,
}: {
  countryCode?: CountryCode;
}) {
  const [services, setServices] = useState<{
    autoCompleteService: google.maps.places.AutocompleteService;
    placesService: google.maps.places.PlacesService;
    geocoder: google.maps.Geocoder;
  }>();
  const [options, setOptions] = useState<AdSuburbViewModel[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const [places, geocoding] = await Promise.all([
          loader.importLibrary("places"),
          loader.importLibrary("geocoding"),
        ]);
        setServices({
          autoCompleteService: new places.AutocompleteService(),
          placesService: new places.PlacesService(
            document.createElement("div") // Create a dummy element for PlacesService
          ),
          geocoder: new geocoding.Geocoder(),
        });
      } catch (ex) {
        console.error("CLIENT_KNOWN_ERROR: google places - loading services", ex);
      }
    })();
  }, []);

  return {
    setInput: async (input: string) => {
      if (!services?.autoCompleteService) {
        console.error(
          "ERROR_KNOWN: google places - cannot provide place predictions, service not loaded"
        );
        return [];
      }
      const response = await services.autoCompleteService.getPlacePredictions({
        input: input,
        componentRestrictions: countryCode
          ? { country: [countryCode.toLowerCase()] }
          : undefined,
        types: ["(regions)"],
      });

      setOptions(
        response.predictions.map((x) => {
          return {
            description: x.description,
            place_id: x.place_id,
            state: "",
            postcode: "",
            suburb: "",
            stateCode: "",
          };
        })
      );
    },
    options: options,
    getPlaceDetails: async (
      input: AdSuburbViewModel | null
    ): Promise<AdSuburbViewModel> => {
      if (!services?.placesService) {
        console.error("CLIENT_KNOWN_ERROR: google places - cannot get place details, service not loaded");
        return {
          description: "",
          postcode: "",
          suburb: "",
          state: "",
          stateCode: "",
          place_id: "",
        };
      }

      if (!input) {
        return {
          description: "",
          postcode: "",
          suburb: "",
          state: "",
          stateCode: "",
          place_id: "",
        };
      }

      return new Promise<AdSuburbViewModel>((resolve, reject) => {
        try {
          services.placesService.getDetails(
            {
              placeId: input.place_id,
              fields: ["address_components", "geometry"],
            },
            async (placeDetails) => {
              try {
                const postcode =
                  placeDetails &&
                  (await getPlaceComponent(placeDetails, services.geocoder, true, "postal_code"));
                const suburb =
                  placeDetails &&
                  (await getPlaceComponent(
                    placeDetails,
                    services.geocoder,
                    true,
                    "administrative_area_level_2",
                    "sublocality_level_1",
                    "locality"
                  ));
                const state =
                  placeDetails &&
                  (await getPlaceComponent(
                    placeDetails,
                    services.geocoder,
                    false,
                    "administrative_area_level_1"
                  ));

                const output: AdSuburbViewModel = {
                  place_id: input?.place_id ?? "",
                  description: input?.description ?? "",
                  postcode: postcode?.long_name ?? "",
                  suburb: suburb?.long_name ?? "",
                  state: state?.long_name ?? "",
                  stateCode: state?.short_name ?? "",
                  lat: placeDetails?.geometry?.location?.lat(),
                  lon: placeDetails?.geometry?.location?.lng(),
                };
                resolve(output);
              } catch (ex) {
                console.error("CLIENT_KNOWN_ERROR: google places - getting place details inner", ex);
                reject(ex);
              }
            }
          );
        } catch (ex) {
          console.error("CLIENT_KNOWN_ERROR: google places - getting place details outer", ex);
          reject(ex);
        }
      });
    },
  };
}
